import React, { FC, useMemo } from 'react';
import {
	CancelUserInvitationDocument,
	RedditUsersListDocument,
	RevokeUserFromGroupDocument,
} from 'wb-reddit/graphql/generated';
import {
	AuthGraphRevokeUserFromGroupDocument,
	AuthGraphRevokeUserFromGroupMutation,
	AuthGraphRevokeUserFromGroupMutationVariables,
	UserGroupAccessRole,
} from 'wb-reddit/graphql/authgraph/generated';
import {
	alephToast,
	BanIcon,
	Button,
	ButtonAppearance, ButtonColor,
	LoaderBars,
	NotificationToast,
	NotificationType,
	isReddit,
	Avatar,
	getAvatarSource,
} from '@aleph/shared-components';
import { MutatingAuthGraphUser, MutatingRedditUser, UserGroupOption } from 'wb-reddit/app/routes/users/mutator/utils';
import { MutationFunction, useMutation } from '@apollo/client';
import { getGroupOwner } from 'wb-reddit/app/routes/teams/utils';
import { Trans, useTranslation } from 'react-i18next';
import { mapTypeNameToAvatarEntity } from 'wb-reddit/app/common/utils';
import { getUserInitials } from 'wb-reddit/app/routes/users/utils';

const GroupListItem: FC<{
	group: UserGroupOption
	user?: MutatingRedditUser | MutatingAuthGraphUser
	onRevokeGroup: (group: UserGroupOption) => void,
	hideActions?: boolean,
}> = ({
	user, group, onRevokeGroup, hideActions,
}) => {
	const { t } = useTranslation();

	const [revokeGroupFromUser, { loading: revokingAccess }] = useMutation(
		isReddit()
			? RevokeUserFromGroupDocument
			: AuthGraphRevokeUserFromGroupDocument,
		{ errorPolicy: 'all' },
	);
	const [cancelGroupInvitation, { loading: invitationCancelling }] = useMutation(CancelUserInvitationDocument, {
		errorPolicy:         'all',
		refetchQueries:      [RedditUsersListDocument],
		awaitRefetchQueries: true,
	});
	// @ts-ignore
	const pendingGroupInvitation = user?.pendingInvites?.find(invite => invite.pendingInviteEntityId === group.id);

	const isOwner = useMemo(() => {
		if (isReddit()) {
			return user?.userId === getGroupOwner(group as UserGroupOption)?.userId;
		}

		// @ts-ignore: is defined, its added in the users list container
		return group?.userGroupAccessRole === UserGroupAccessRole.Owner;
	}, [group, user]);

	const onGroupRevokedFromUser = async () => {
		if (!user?.userId) {
			onRevokeGroup(group);

			return;
		}

		if (isReddit()) {
			const { errors: revokeErrors } = pendingGroupInvitation
				? { errors: undefined }
				: await revokeGroupFromUser({
					variables: {
						userId:      user?.userId,
						// @ts-ignore
						userGroupId: group.id,
					},
				});

			const { errors: cancelErrors } = !pendingGroupInvitation
				? { errors: undefined }
				: await cancelGroupInvitation({
					variables: {
						pendingInviteHash: pendingGroupInvitation?.pendingInviteHash,
					},
				});

			if (cancelErrors || revokeErrors) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={t('sc: action err', { action: 'revoking group from user' })}
					/>,
				);

				return;
			}

			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					title={
						<div>
							<Trans
								i18nKey="sc: revoke_access.success"
								defaults="User <strong>{{userName}}</strong> has been revoked access from team <strong>{{groupName}}</strong>"
								values={{ userName: user?.userName, groupName: group.label }}
							>
								User <strong>{user?.userName}</strong>
								&nbsp;
								has been revoked access from team <strong>{group.label}</strong>
							</Trans>
						</div>
					}
				/>,
			);
		}

		const authGraphRevokeUserFromGroupMutation = revokeGroupFromUser as MutationFunction<AuthGraphRevokeUserFromGroupMutation, AuthGraphRevokeUserFromGroupMutationVariables>;

		const { errors: revokeErrors } = await authGraphRevokeUserFromGroupMutation({
			variables: {
				userId:      user?.userId,
				// @ts-ignore
				userGroupId: group.id,
			},
		});

		if (revokeErrors) {
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={t('sc: action err', { action: 'revoking group from user' })}
				/>,
			);

			return;
		}

		alephToast(
			<NotificationToast
				type={NotificationType.Success}
				title={
					<Trans
						i18nKey="sc: revoke_access.success"
						defaults="User <strong>{{userName}}</strong> has been revoked access from team <strong>{{groupName}}</strong>"
						values={{ userName: user?.userName, groupName: group.label }}
					>
						User <strong>{user?.userName}</strong>
						&nbsp;
						has been revoked access from team <strong>{group.label}</strong>
					</Trans>
				}
			/>,
		);

		// @ts-ignore
		onRevokeGroup(group);
	};

	return (
		<div
			className="h-[48px] whitespace-nowrap flex px-[8px] items-center gap-[8px] border-b border-b-gray-200
				last:border-0 hover:bg-gray-100"
		>
			<div className="flex flex-1 gap-[6px] items-center h-full overflow-hidden">
				<div className="relative">
					<Avatar
						src={getAvatarSource(group)}
						entityType={mapTypeNameToAvatarEntity(group)}
						entityUserInitials={getUserInitials(group)}
					/>
				</div>
				<div className="overflow-hidden overflow-ellipsis">
					{group.label}
				</div>
			</div>

			{pendingGroupInvitation
				&& <div className="flex text-gray-600 text-[12px] justify-center w-[60px]">
					Pending
				</div>
			}

			{!hideActions
				&& <div className="flex justify-center w-[60px]">
					{invitationCancelling || revokingAccess
						? (
							<Button
								leftNode={
									<LoaderBars
										className="animate-spin"
									/>
								}
								disabled
							/>
							// @ts-ignore
						) : isOwner
							? <span className="text-gray-600 text-[12px]">Owner</span>
							:	<Button
								leftNode={<BanIcon />}
								appearance={ButtonAppearance.GHOST}
								color={ButtonColor.GRAY}
								onClick={onGroupRevokedFromUser}
							/>
					}
				</div>
			}
		</div>
	);
};

export default GroupListItem;
